import { useCallback, useEffect, useState } from 'react';
import { createRemoteNode, fetchRemoteNodes, fetchRemoteNodeStatus } from '../lib/api';
import type { RemoteNode } from '../lib/api';

export interface RemoteNodesState {
  nodes: RemoteNode[];
  loading: boolean;
  loadError: string | null;
  onlineById: Record<string, boolean>;
  connectionTokens: Record<string, string>;
  createNode: (machineName: string) => Promise<RemoteNode>;
  recordConnectionToken: (nodeId: string, connectionToken: string) => void;
  forgetDeletedNode: (nodeId: string) => void;
  reset: () => void;
}

const withoutKey = <T,>(map: Record<string, T>, key: string): Record<string, T> =>
  Object.fromEntries(Object.entries(map).filter(([entryKey]) => entryKey !== key));

// Single owner of the user's remote nodes: the list, each node's online state,
// and the connection tokens that live only in memory because no read path
// returns them. `active` means the computers surface is on screen.
export function useRemoteNodes(active: boolean): RemoteNodesState {
  const [nodes, setNodes] = useState<RemoteNode[]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [onlineById, setOnlineById] = useState<Record<string, boolean>>({});
  const [connectionTokens, setConnectionTokens] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!active) {
      setListLoaded(false);
      return;
    }
    let cancelled = false;
    setLoadError(null);
    fetchRemoteNodes()
      .then((loaded) => {
        if (cancelled) return;
        setNodes(loaded);
        setListLoaded(true);
        return Promise.all(
          loaded.map((node) =>
            fetchRemoteNodeStatus(node.id).then(
              (status) => [node.id, status.online] as const,
              () => [node.id, false] as const,
            ),
          ),
        ).then((statuses) => {
          if (cancelled) return;
          setOnlineById(Object.fromEntries(statuses));
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setListLoaded(true);
        setLoadError(err instanceof Error ? err.message : 'Could not load your computers.');
      });
    return () => { cancelled = true; };
  }, [active]);

  const createNode = useCallback(async (machineName: string) => {
    const { node, connectionToken } = await createRemoteNode(machineName);
    setNodes((prev) => [node, ...prev.filter((existing) => existing.id !== node.id)]);
    setOnlineById((prev) => ({ ...prev, [node.id]: false }));
    setConnectionTokens((prev) => ({ ...prev, [node.id]: connectionToken }));
    return node;
  }, []);

  const recordConnectionToken = useCallback((nodeId: string, connectionToken: string) => {
    setConnectionTokens((prev) => ({ ...prev, [nodeId]: connectionToken }));
  }, []);

  const forgetDeletedNode = useCallback((nodeId: string) => {
    setNodes((prev) => prev.filter((node) => node.id !== nodeId));
    setOnlineById((prev) => withoutKey(prev, nodeId));
    setConnectionTokens((prev) => withoutKey(prev, nodeId));
  }, []);

  const reset = useCallback(() => {
    setNodes([]);
    setListLoaded(false);
    setLoadError(null);
    setOnlineById({});
    setConnectionTokens({});
  }, []);

  return {
    nodes,
    loading: active && !listLoaded,
    loadError,
    onlineById,
    connectionTokens,
    createNode,
    recordConnectionToken,
    forgetDeletedNode,
    reset,
  };
}
